import type {SigningFormat} from "./format.util";
import {DigitalSigningTipRequest, SnapshotTip, SnapshotTipOption, TipFormat, TipMode} from "./digitalSigning.types";

/*
    Tip arithmetic in integer cents. Preset amounts come from the snapshot (the server computed them
    and checks them on sign); only a custom amount is parsed here.
*/

/** What the guest picked. A custom amount stays text until it parses. */
export type TipChoice =
    | {mode: 'PRESET', presetIndex: number}
    | {mode: 'CUSTOM'}
    | {mode: 'NONE'};

/** A complete tip: what sign sends and what the total adds. */
export interface ResolvedTip {
    mode: TipMode
    presetIndex?: number
    amountCents: number
}

export type CustomTipProblem = 'INVALID' | 'NOT_POSITIVE';

export const NO_TIP: ResolvedTip = {mode: 'NONE', amountCents: 0};

// Up to 7 whole digits, then `,` or `.` and at most two decimals
const AMOUNT = /^(\d{0,7})(?:[.,](\d{0,2}))?$/;
// The decimal mark typed, its decimals not yet: "15," on the way to "15,50"
const UNFINISHED_AMOUNT = /^\d{0,7}[.,]$/;

/** Parses a typed amount string (e.g. "12,50" or "12.50") into cents. Returns null for invalid input. */
export const parseAmountToCents = (input: string): number | null => {
    const match = AMOUNT.exec(input.trim());
    if (!match) return null;

    const [, whole, fraction = ''] = match;
    if (!whole && !fraction) return null;

    return Number(whole || '0') * 100 + Number(fraction.padEnd(2, '0'));
};

/** True if the amount string ends in a decimal mark (e.g. "15,"). */
export const isUnfinishedAmount = (input: string): boolean => UNFINISHED_AMOUNT.test(input.trim());

/** Custom tip in cents: null while the amount is unfinished or invalid. */
export const customTipCents = (input: string): number | null => (
    isUnfinishedAmount(input) ? null : parseAmountToCents(input)
);

/** Strips trailing decimal mark when leaving the field. */
export const finishAmountInput = (input: string): string => (
    isUnfinishedAmount(input) ? input.trim().slice(0, -1) : input
);

/** Returns validation error if the custom tip amount is null or not positive. */
export const customTipProblem = (cents: number | null): CustomTipProblem | undefined => {
    if (cents === null) return 'INVALID';
    if (cents <= 0) return 'NOT_POSITIVE';
    return undefined;
};

/** The preselected preset (only hotels outside the EU/EEA/CH have one), else No tip. */
export const initialTipChoice = (tip: SnapshotTip | undefined): TipChoice => {
    const index = tip?.preselectedPresetIndex;
    if (index === undefined || index === null || !tip?.options?.some(option => option.index === index)) {
        return {mode: 'NONE'};
    }
    return {mode: 'PRESET', presetIndex: index};
};

/** Preset pill label: percentage for percent preset, short money for fixed amount. */
export const tipPresetLabel = (
    option: SnapshotTipOption,
    appliedFormat: TipFormat,
    format: Pick<SigningFormat, 'moneyShort' | 'percent'>
): string => (
    appliedFormat === 'PERCENT' && typeof option.percent === 'number'
        ? format.percent(option.percent)
        : format.moneyShort(option.amountCents)
);

/** What the line under the pills says of a chosen percent preset: "€13.00 · 5% of €250.00". */
export interface TipPresetDetail {
    amountCents: number
    percent: number
    baseCents: number
    /** The amount is not the share of the base to the cent: the settings round to a step. */
    rounded: boolean
}

/** The line under the pills for [option] - only a percent preset has one. */
export const tipPresetDetail = (
    option: SnapshotTipOption,
    tip: Pick<SnapshotTip, 'appliedFormat' | 'baseCents'>
): TipPresetDetail | undefined => {
    if (tip.appliedFormat !== 'PERCENT' || typeof option.percent !== 'number') return undefined;

    // To the cent, half up - exactly as the server computed the amount before its rounding step
    const exactCents = Math.round(tip.baseCents * option.percent / 100);
    return {amountCents: option.amountCents, percent: option.percent, baseCents: tip.baseCents, rounded: option.amountCents !== exactCents};
};

/** The complete tip for [choice], or undefined while there is none or the custom amount is unfinished or invalid. */
export const resolveTip = (
    tip: SnapshotTip | undefined,
    choice: TipChoice | undefined,
    customInput: string
): ResolvedTip | undefined => {
    if (!choice) return undefined;
    if (choice.mode === 'NONE') return NO_TIP;

    if (choice.mode === 'PRESET') {
        const option = tip?.options?.find(candidate => candidate.index === choice.presetIndex);
        return option ? {mode: 'PRESET', presetIndex: option.index, amountCents: option.amountCents} : undefined;
    }

    if (!tip?.customAllowed) return undefined;
    const cents = customTipCents(customInput);
    return cents !== null && !customTipProblem(cents)
        ? {mode: 'CUSTOM', amountCents: cents}
        : undefined;
};

/** Identifies a tip; the signature is cleared whenever it changes. */
export const tipKey = (tip: ResolvedTip | undefined): string => (
    tip ? `${tip.mode}:${tip.presetIndex ?? ''}:${tip.amountCents}` : ''
);

/** The sign request's tip: index and amount for a preset, the amount for custom, nothing for no tip. */
export const tipRequest = (tip: ResolvedTip): DigitalSigningTipRequest => {
    if (tip.mode === 'PRESET') return {mode: 'PRESET', presetIndex: tip.presetIndex, amountCents: tip.amountCents};
    if (tip.mode === 'CUSTOM') return {mode: 'CUSTOM', amountCents: tip.amountCents};
    return {mode: 'NONE'};
};

/** [amountCents] as a percentage of [baseCents], rounded to two decimals like the server; undefined without a base. */
export const tipPercent = (amountCents: number, baseCents: number): number | undefined => (
    baseCents > 0 ? Math.round(amountCents * 10000 / baseCents) / 100 : undefined
);

export const grandTotalCents = (totalCents: number, tip: ResolvedTip | undefined): number => (
    totalCents + (tip?.amountCents ?? 0)
);
